import IconButton from "@pathwright/ui/src/components/button/IconButton"
import DropdownMenu from "@pathwright/ui/src/components/dropdown/DropdownMenu"
import DropdownTrigger from "@pathwright/ui/src/components/dropdown/DropdownTrigger"
import CountIndicator from "@pathwright/ui/src/components/indicator/CountIndicator"
import withTranslate from "@pathwright/ui/src/components/lng/withTranslate"
import { getReviewRoute } from "@pathwright/web/src/modules/inbox/routes"
import StepPublishCard from "@pathwright/web/src/modules/step/publish/StepPublishCard"
import { BELLTOWER_SCHOOL_ID } from "@pathwright/web/src/modules/store/constants"
import classnames from "classnames"
import { ConnectStores } from "lib/core/store"
import { printContent } from "lib/core/utils/print"
import * as urls from "lib/core/utils/urls"
import pathUtils from "path-browserify"
import { DESIGN, LEARN, TEACH } from "path/constants"
import PropTypes from "prop-types"
import { Component } from "react"
import styled from "styled-components"
import {
  CopyItemComponent,
  DatesItemComponent,
  DragItemComponent,
  DropdownItemComponent,
  InlineItemComponent
} from "./PathItemControlComponents"
import "./PathItemControls.css"

const StyledPathItemControls = styled.div`
  .chakra-popover__popper {
    z-index: 100;
  }
`

// Component concerns:
// - Handles rendering & configuration separately from logic determining which controls to display.
// - Higher level component should handle which controls to show (via screensize, permissions, etc.)

class PathItemControls extends Component {
  shouldComponentUpdate() {
    // TODO: perf updates
    return true
  }

  _getStyles = () => {
    return _.defaults({}, this.props.buttonStyles, {
      styleType: "blank",
      size: "medium"
    })
  }

  _getComponent = (Name, inline) => {
    switch (Name) {
      case "Move":
        return DragItemComponent
      case "Copy":
        if (inline) {
          return CopyItemComponent
        } else {
          return DropdownItemComponent
        }
      case "Dates":
        if (inline) {
          return DatesItemComponent
        } else {
          return DropdownItemComponent
        }
      default:
        if (inline) {
          return InlineItemComponent
        } else {
          return DropdownItemComponent
        }
    }
  }

  renderControl = (Name, inline) => {
    const Component = this._getComponent(Name, inline)
    const className = `PathItemControl PathItemControl__${Name}`
    const passProps = this.props[`get${Name}Props`]()
    const styles = this._getStyles()

    return passProps ? (
      <Component
        {...passProps}
        className={className}
        key={passProps.label}
        styles={styles}
      />
    ) : null
  }

  renderIndicator = () => {
    const count = this.props.getIndicatorCount(this.props.inlineControls)
    return (
      <CountIndicator className="PathItemControls__indicator" count={count} />
    )
  }

  renderInlineControls = () => {
    return this.props.inlineControls.map((Name) => {
      return this.renderControl(Name, true)
    })
  }

  renderDropdownControls = () => {
    return this.props.dropdownControls.map((Name) => {
      return this.renderControl(Name, false)
    })
  }

  renderDropdown = () => {
    const styles = this._getStyles()

    return (
      <DropdownTrigger
        renderDropdown={() => {
          return (
            <DropdownMenu closeOnClick={true}>
              {this.renderDropdownControls()}
            </DropdownMenu>
          )
        }}
      >
        <IconButton {...Object.assign({}, styles, { icon: "chevron-down" })} />
      </DropdownTrigger>
    )
  }

  render() {
    return (
      <StyledPathItemControls
        className={classnames("PathItemControls", this.props.className)}
      >
        {(this.props.inlineControls &&
          this.props.inlineControls.length &&
          this.renderInlineControls()) ||
          null}
        {(this.props.dropdownControls &&
          this.props.dropdownControls.length &&
          this.renderDropdown()) ||
          null}
        {(this.props.showIndicator(this.props.inlineControls) &&
          this.renderIndicator()) ||
          null}
        {this.props.mode === DESIGN && (
          <StepPublishCard
            offering={this.props.offering}
            item={this.props.item}
          />
        )}
      </StyledPathItemControls>
    )
  }
}

PathItemControls.displayName = "PathItemControls"

PathItemControls.propTypes = {
  // Controls to display inline.
  inlineControls: PropTypes.arrayOf(
    PropTypes.oneOf([
      "Notes", // All modes in standalone/content step view only (or Teacher path group view)
      "Print", // Learner in standalone/content step view only

      // Teach mode
      "Stats",
      "Review", // Inbox
      "Dates", // Scheduler

      // Design mode
      "Edit", // Also appears in standalone/content step mode
      "Move",
      "Copy",
      "Archive",
      "Settings" // Step only
    ])
  ),
  // Controls to dislay in dropdown.
  dropdownControls: PropTypes.arrayOf(
    PropTypes.oneOf([
      "Copy",
      "Settings",
      "Edit",
      "Archive",
      "Stats",
      "Review",
      "Dates",
      "Notes",
      "Publish"
    ])
  ),
  // The path item (topic/step/group)
  item: PropTypes.object.isRequired,
  // Connector for Move controls
  dragHandleProps: PropTypes.object,
  // See Button for acceptable props
  buttonStyles: PropTypes.object
}

const mapStateToProps = function (state, ownProps) {
  const { selectPathItemForEditing, selectPathItemForArchiving } =
    state.pathAdminStore.action
  const { getAnalyticsForItem } = state.pathAnalyticsStore.request
  const { notes_per_target } = state.noteStore.state
  const { resource, offering } = state.resourceStore.state
  const { user } = state.authStore.state
  const { can_edit_path } = state.resourceStore.request.permissions()
  const { navigate } = state.navigationStore.action
  const { setMode } = state.pathStore.action
  const { path, mode } = state.pathStore.state
  const { item, dragHandleProps, t } = ownProps

  const analytics = getAnalyticsForItem(item.sync_source_id)
  const stepURL = item != null ? item.url : undefined

  // getter functions provide props specially made to fit into
  // generic inline/dropdown item components. custom props and
  // components can also be provided (see Move, Dates, etc.)

  const getEditProps = () => ({
    icon: "edit",
    label: `${t("path.controls.edit")}`,
    handler: () => selectPathItemForEditing(item)
  })

  const getCopyProps = () =>
    // only show copy button to superusers on belltower licensed courses
    resource.licensed_from_school === BELLTOWER_SCHOOL_ID &&
    !(user && user.is_superuser)
      ? null
      : {
          icon: "copy",
          label: `${t("path.controls.copy")}`,
          item: item,
          handler: () => {}
        }

  const getArchiveProps = () => ({
    icon: "trash",
    label: `${t("path.controls.delete")}`,
    handler: () => selectPathItemForArchiving(item)
  })

  const getSettingsProps = () => ({
    icon: "gear",
    label: `${t("path.controls.settings")}`,
    to: { pathname: stepURL + "settings/", state: { modal: true } }
  })

  const getPublishProps = () => ({
    icon: "arrow-up-circle",
    label: `${t("path.controls.publish")}`,
    to: urls.modalRoute(`${pathUtils.join(stepURL, "../../")}publish/`)
  })

  const getMoveProps = () => ({
    dragHandleProps,
    icon: "arrow-double-vertical-2",
    label: `${t("path.controls.reorder")}`
  })

  const getDatesProps = function () {
    const url = `/library/${resource.slug}/${offering.id}/path/schedule/${item.sync_source_id}/`

    return {
      label: `${t("path.controls.schedule")}`,
      mode,
      item,
      to: url
    }
  }

  const getNotesProps = function () {
    const notes = __guard__(
      notes_per_target != null
        ? notes_per_target[item.sync_source_id]
        : undefined,
      (x) => x.total_notes
    )
    const url =
      item.item_type === "G"
        ? `${offering.url}path/notes/${item.sync_source_id}/`
        : `${stepURL}notes/`

    return {
      icon: "note",
      label: `${t("path.controls.notes")}`,
      count: notes,
      to: url
    }
  }

  const getPrintProps = function () {
    return {
      icon: "printer",
      label: `${t("path.controls.print")}`,
      className: "PathItemControl__Print",
      handler: () => printContent(".Step.Step--is-active .Step__ContentWrap"),
      // Ugly, but default control to be hidden for learners/moderators.
      // This style can be overridden by the school with `.PathItemControl__Print { display: block !important; }`
      style:
        mode !== LEARN || !can_edit_path
          ? {
              display: "none"
            }
          : {}
    }
  }

  const getStatsProps = function () {
    if ((analytics != null ? analytics.next_count : undefined) == null) {
      return null
    }
    const { next_count, complete } = analytics
    const completion_rate = complete / (next_count + complete) || 0 // 0 / 0 //=> NaN, so we prefer 0 to NaN :)
    return {
      icon: "gauge-square",
      label: `${t("path.controls.stats")}`,
      to: `${path != null ? path.url : undefined}analytics/${
        item.sync_source_id
      }/`,
      count: `${Math.floor(completion_rate * 100)}%`
      // indicator: completion_rate < 0.6
    }
  }

  const getReviewProps = function () {
    // const params = { step: item.sync_source_id }
    // if ((analytics != null ? analytics.need_feedback : undefined) > 0) {
    //   params.filter = "review"
    // }
    // const toReview = `${path != null ? path.url : undefined}${
    //   path != null ? path.id : undefined
    // }/completion/`
    let params = { cohort_id: offering.id }
    if (item.item_type === "G") {
      params.parent_id = item.sync_source_id
    } else {
      params.source_id = item.sync_source_id
    }
    if (analytics && analytics.need_feedback > 0) {
      params.filter = "pending"
    }
    const toReview = {
      pathname: getReviewRoute(),
      query: params,
      state: { setLaunchedFrom: true }
    }

    return {
      icon: "inbox",
      label: `${t("path.controls.review")}`,
      to: toReview,
      count: analytics != null ? analytics.need_feedback : undefined,
      indicator: (analytics != null ? analytics.need_feedback : undefined) > 0
    }
  }

  const getIndicatorCount = function (inlineControls) {
    if (analytics == null) {
      return null
    }
    const { need_feedback } = analytics
    const hasReview = _.includes(inlineControls, "Review") && need_feedback > 0
    if (hasReview) {
      return need_feedback
    } else {
      return null
    }
  }

  const showIndicator = function (inlineControls) {
    if (analytics == null || offering.is_curriculum || mode !== TEACH) {
      return false
    }
    const { next_count, complete, need_feedback } = analytics
    // completionRate = complete / (next_count + complete)
    // hasStats = _.includes(inlineControls, "Stats") and completionRate < 0.6
    const hasReview = !_.includes(inlineControls, "Review") && need_feedback > 0
    return hasReview
  }

  return _.assign(
    {},
    {
      getPrintProps,
      getNotesProps,
      getCopyProps,
      getEditProps,
      getArchiveProps,
      getSettingsProps,
      getPublishProps,
      getMoveProps,
      getDatesProps,
      getStatsProps,
      getReviewProps,
      getIndicatorCount,
      showIndicator,
      offering,
      resource,
      mode
    },
    ownProps
  )
}

const stores = [
  { store: "auth", watch: false },
  { store: "resource", watch: false },
  { store: "pathAdmin", watch: false },
  { store: "path", watch: ["path", "mode"] },
  { store: "navigation", watch: false },
  { store: "note", watch: ["notes_per_target"] },
  { store: "pathAnalytics", watch: ["item_analytics"] }
]

export default withTranslate(
  ConnectStores(PathItemControls, stores, {}, mapStateToProps)
)

function __guard__(value, transform) {
  return typeof value !== "undefined" && value !== null
    ? transform(value)
    : undefined
}
